import React, { useEffect, useRef } from "react";
import useDraggablePanel from "../hooks/useDraggablePanel";

// A modeless, draggable panel. Unlike a modal it has no backdrop, so the page
// underneath stays scrollable and clickable while the panel is open — the point
// being to read the panel against the page rather than instead of it. Drag it
// by the header to uncover whatever it is covering.
//
// The consumer supplies the body; the wrapper, header, drag handle, close
// button and Escape handling belong to this component. Anything that needs
// different chrome can use `useDraggablePanel` directly instead.
const FloatingPanel = ({
  isOpen,
  onClose,
  title,
  subtitle,
  width = 900,
  height = 520,
  bodyProps = {},
  children,
}) => {
  const { panelRef, panelStyle, handleProps, isDragging } = useDraggablePanel({
    isOpen,
  });

  // Clicking away cannot close a panel that has no backdrop to click, so
  // Escape takes over as the keyboard route out. Held in a ref because callers
  // pass an inline arrow, which would resubscribe the listener every render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onCloseRef.current?.();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  if (!isOpen) return null;

  const {
    className: bodyClassName = "",
    style: bodyStyle,
    ...restBodyProps
  } = bodyProps;

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label={title}
      className={`floating-panel ${
        isDragging ? "floating-panel--dragging" : ""
      }`}
      style={{
        width: `${width}px`,
        maxWidth: "calc(100vw - 32px)",
        ...panelStyle,
      }}
    >
      <div
        className="modal-content d-flex flex-column pb-15"
        style={{
          height: `${height}px`,
          width: "100%",
          borderRadius: "10px",
          boxShadow: "0 12px 32px rgba(0, 0, 0, 0.25)",
        }}
      >
        <div
          className="modal-header border-0 pb-0 floating-panel__handle"
          {...handleProps}
        >
          <div className="position-relative w-100">
            <h5 className="text_primary font-weight-bold text-center my-25">
              {title}
            </h5>
            {subtitle && (
              <small className="text-muted d-block mb-31">{subtitle}</small>
            )}
            <button
              type="button"
              className="close"
              aria-label="Close"
              onClick={onClose}
              style={{ position: "absolute", top: "15px", right: "15px" }}
            >
              <span
                className="text_primary"
                style={{ fontSize: 40, fontWeight: "600" }}
                aria-hidden="true"
              >
                &times;
              </span>
            </button>
          </div>
        </div>

        <div
          className={`modal-body pt-2 ${bodyClassName}`}
          style={{ flex: 1, overflowY: "auto", ...bodyStyle }}
          {...restBodyProps}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default FloatingPanel;
